import { action, computed, flow, makeObservable, observable } from 'mobx';
import { GlobalStore, UserStore } from '@roc/feature-app-core';
import { LoanStore } from '@roc/feature-loans';
import { FormStore } from '@roc/feature-app-core';
import { Document } from '@roc/feature-types';
import { TitleCompanyBankInformationService } from '../../../services/documentForms/titleCompanyBankInformationService';
import { downloadDocument, parseAddress } from '@roc/feature-utils';
import { DocumentStore } from '../../documentStore';
import { ATTORNEY_TITLE_ENTITY_TYPE } from '../../../constants';

const BANK_INFO_ATTORNEY_TITLE_ENTITY_TYPE = 'AttorneyTitleEntity';
const GERACI_LAW_FIRM = 'Geraci Law Firm';

const form = {
  fields: {
    entityName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'title company name',
    },
    bankName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'bank name',
    },
    routingNumber: {
      value: '',
      error: null,
      rule: 'required|regex:/\\d{9}/',
      attribute: 'routing number',
    },
    accountName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'account name',
    },
    accountNumber: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'account number',
    },
    referenceId: {
      value: '',
      error: null,
      rule: '',
      attribute: 'reference ID',
    },
    address: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'address',
    },
    fullName: {
      value: '',
      error: null,
      rule: '',
      attribute: 'authorized contact name',
    },
    authorizedContactTitle: {
      value: '',
      error: null,
      rule: '',
      attribute: 'authorized contact title',
    },
    phone: {
      value: '',
      error: null,
      rule: '',
      attribute: 'direct phone',
    },
    email: {
      value: '',
      error: null,
      rule: '',
      attribute: 'email address',
    },
    titleLicense: {
      value: '',
      error: null,
      rule: '',
      attribute: 'title license / escrow number',
    },
    swiftCode: {
      value: '',
      error: null,
      rule: '',
      attribute: 'swift / bic code',
    },
    intermediaryBankName: {
      value: '',
      error: null,
      rule: '',
      attribute: 'intermediary bank name',
    },
    intermediaryRoutingNumber: {
      value: '',
      error: null,
      rule: '',
      attribute: 'intermediary routing number',
    },
    city: {
      value: '',
      error: null,
      rule: '',
    },
    zipCode: {
      value: '',
      error: null,
      rule: '',
    },
    state: {
      value: '',
      error: null,
      rule: '',
    },
    streetName: {
      value: '',
      error: null,
      rule: '',
    },
    ffcBankName: {
      value: '',
      error: null,
      rule: '',
    },
    ffcRoutingNumber: {
      value: '',
      error: null,
      rule: '',
    },
    ffcAccountName: {
      value: '',
      error: null,
      rule: '',
    },
    ffcAccountNumber: {
      value: '',
      error: null,
      rule: '',
    },
  },
  meta: {
    isValid: true,
    error: null,
  },
};

export class TitleCompanyBankInformationStore extends FormStore {
  loanStore: LoanStore;
  documentStore: DocumentStore;
  globalStore: GlobalStore;
  titleCompanyBankInformationService: TitleCompanyBankInformationService;
  editEnabled: boolean;

  bankInfo;
  wireInstructionsDocumentName: string | null;

  constructor(
    loanStore: LoanStore,
    documentStore: DocumentStore,
    globalStore: GlobalStore
  ) {
    super(form, globalStore);
    this.loanStore = loanStore;
    this.documentStore = documentStore;
    this.globalStore = globalStore;
    this.titleCompanyBankInformationService = new TitleCompanyBankInformationService();
    this.editEnabled = false;
    this.wireInstructionsDocumentName = null;
    makeObservable(this, {
      bankInfo: observable,
      editEnabled: observable,
      wireInstructionsDocumentName: observable,
      canEdit: computed,
      canSubmit: computed,
      isGeraci: computed,
      resetStore: action,
      initialize: flow,
      submit: flow,
      saveWireInformation: flow,
      updateTitleCompanyBankInfo: flow,
      initializeForLender: flow,
      setEditEnabled: action,
      rejectBankInformation: flow,
      approveBankInformation: flow,
      fetchTaskDocument: flow,
      downloadWireInstructionsDocument: flow,
    });
  }

  *initialize() {
    try {
      this.resetStore();
      const { loanId } = this.loanStore.loanDetails;
      const response = yield this.titleCompanyBankInformationService.getTitleCompanyBankInfo(
        loanId
      );
      this.bankInfo = response.data.data || {};
      this.loadForm(this.bankInfo);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error trying to load the bank info data',
      });
    }
  }

  *initializeForLender() {
    try {
      this.resetStore();
      const companyId = this.loanStore.userStore.userInformation.companyId ?? this.globalStore.selectedCompanyId;
      const response = yield this.titleCompanyBankInformationService.getTitleCompanyBankInfoForLender(
        companyId
      );
      this.bankInfo = response.data || {};
      this.loadForm(this.bankInfo);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error trying to load the bank info data',
      });
    }
  }

  resetStore() {
    this.bankInfo = null;
    this.editEnabled = false;
    this.wireInstructionsDocumentName = null;
    this.reset();
  }

  *submit() {
    this.runFormValidationWithMessage();
    if (this.canSubmit && this.form.meta.isValid) {
      try {
        const { loanId } = this.loanStore.loanDetails;
        const data = this.getPayload();
        yield this.titleCompanyBankInformationService.updateTitleCompanyBankInfo(
          loanId,
          data
        );
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Request made successfuly',
        });
        yield this.documentStore.documentFormStore.closeCurrentForm();
        yield this.loanStore.refreshLoanDetails();
      } catch (error) {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'There was an error trying to make the request',
        });
      }
    }
  }

  *saveWireInformation() {
    this.runFormValidationWithMessage();
    if (!this.form.meta.isValid) {
      return;
    }
    try {
      const { loanId } = this.loanStore.loanDetails;
      const data = JSON.stringify(this.getFormValues());
      yield this.titleCompanyBankInformationService.updateWireInformation(
        loanId,
        data
      );
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Wire information saved successfully',
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error trying to save the wire information',
      });
    }
  }

  *updateTitleCompanyBankInfo() {
    this.runFormValidationWithMessage();
    try {
      const data = this.getPayload();
      const companyId = this.loanStore.userStore.userInformation.companyId ?? this.globalStore.selectedCompanyId;
      yield this.titleCompanyBankInformationService.updateTitleCompanyBankInfoLenderEntity(
        companyId,
        data
      );
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Request made successfuly',
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error trying to make the request',
      });
    }
  }

  private getPayload() {
    return {
      ...this.bankInfo,
      ...this.getFormValues(),
    };
  }

  handleAddressChange(address: string, addressDetails, geometryDetails) {
    const parsedAddress = parseAddress(
      address,
      addressDetails,
      geometryDetails
    );

    this.onFieldChange('city', parsedAddress.city);
    this.onFieldChange('zipCode', parsedAddress.zip);
    this.onFieldChange('state', parsedAddress.state);
    this.onFieldChange(
      'streetName',
      `${parsedAddress?.street_number} ${parsedAddress?.street}`
    );
  }

  get isGeraci() {
    const { entityType, entityName } = this.bankInfo || {};
    return (
      entityType === BANK_INFO_ATTORNEY_TITLE_ENTITY_TYPE &&
      entityName === GERACI_LAW_FIRM
    );
  }
  get canEdit() {
    return this.globalStore.userFeatures.isClosingAttorney ||
      (this.globalStore.userFeatures?.closingEntitlements.isTitleCompanyBankInfoReviewAllowed && this.editEnabled);
  }

  get canSubmit() {
    return this.canEdit && this.form.meta.isValid;
  }

  setEditEnabled = (value: boolean) => {
    this.editEnabled = value;
  }

  *rejectBankInformation() {
    try {
      const { loanId } = this.loanStore.loanDetails;
      yield this.titleCompanyBankInformationService.rejectEntityBankInformation(loanId, ATTORNEY_TITLE_ENTITY_TYPE, '');
      yield this.documentStore.documentFormStore.closeCurrentForm();
      yield this.loanStore.refreshLoanDetails();
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'The bank information has been rejected successfuly',
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error trying to reject the bank information',
      })
    }
  }

  *approveBankInformation() {
    try {
      const { loanId } = this.loanStore.loanDetails;
      yield this.titleCompanyBankInformationService.approveEntityBankInformation(loanId, ATTORNEY_TITLE_ENTITY_TYPE);
      yield this.documentStore.documentFormStore.closeCurrentForm();
      yield this.loanStore.refreshLoanDetails();
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'The bank information has been approved successfuly',
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error trying to approve the bank information',
      })
    }
  }

  *fetchTaskDocument(taskId: number) {
    try {
      const response = yield this.titleCompanyBankInformationService.getTaskDocuments(taskId);
      const docs = response?.data?.data;
      this.wireInstructionsDocumentName = docs?.[0]?.originalFileName || null;
    } catch {
      this.wireInstructionsDocumentName = null;
    }
  }

  *downloadWireInstructionsDocument(taskId: number) {
    try {
      const response = yield this.titleCompanyBankInformationService.downloadWireInstructionsDocument(taskId);
      const doc = response?.data?.data;
      if (doc?.docBytesArray) {
        const bytes = Uint8Array.from(atob(doc.docBytesArray), c => c.charCodeAt(0));
        downloadDocument(bytes, { 'content-type': doc.contentType }, 'download', doc.originalFileName || 'Wire_Instructions.pdf');
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error trying to download the wire instructions document',
      });
    }
  }
}
